import { Placeholder } from "react-bootstrap";

import {
  AttributesView,
  BreakdownBar,
  useModelColorMap,
} from "@bagel-web/components";

import type { MetricUsage, UsageMetricMetadata } from "../types";
import {
  BarReportSectionContainer,
  BarReportSectionHeader,
  ReportLabel,
  ReportSectionContainer,
} from "./BarReport";
import { formatCurrency, formatNumber } from "../util";

type SummarySection = {
  title: string;
  scope: string;
  timestamp?: string;
};

const getMetricValue = (usage: MetricUsage, metricKey: string) =>
  Number(usage.metrics[metricKey as keyof typeof usage.metrics] ?? 0);

const mergeUsage = (left: MetricUsage, right: MetricUsage): MetricUsage => ({
  ...left,
  metrics: Object.fromEntries(
    Array.from(
      new Set([...Object.keys(left.metrics), ...Object.keys(right.metrics)])
    ).map((metricKey) => [
      metricKey,
      getMetricValue(left, metricKey) + getMetricValue(right, metricKey),
    ])
  ),
});

type OverlayMetric = Pick<UsageMetricMetadata, "key" | "display_name">;

const UsageTooltip = ({
  usage,
  metricLabel,
  metricKey,
  overlayMetric,
  getCost,
}: {
  usage: MetricUsage;
  metricLabel: string;
  metricKey: string;
  overlayMetric?: OverlayMetric;
  getCost?: (usage: MetricUsage[] | MetricUsage) => number;
}) => {
  const attributes = [
    {
      name: "model",
      label: "Service",
      value: usage.service_alias_name,
      showMonoValue: true,
      showBold: false,
    },
    {
      name: metricKey,
      label: metricLabel,
      value:
        metricKey === "cost" && getCost
          ? formatCurrency(getCost(usage))
          : formatNumber(getMetricValue(usage, metricKey)),
      showMonoValue: false,
      showBold: true,
    },
  ];
  if (overlayMetric) {
    attributes.push({
      name: overlayMetric.key,
      label: overlayMetric.display_name,
      value: formatNumber(getMetricValue(usage, overlayMetric.key)),
      showMonoValue: false,
      showBold: false,
    });
  }

  return <AttributesView attributes={attributes} />;
};

const UsageSummaryBar = ({
  usage,
  metricKey,
  metricLabel,
  overlayMetric,
  getCost,
}: {
  usage?: MetricUsage[];
  metricKey: string;
  metricLabel: string;
  overlayMetric?: OverlayMetric;
  getCost?: (usage: MetricUsage[] | MetricUsage) => number;
}) => {
  const { getColors } = useModelColorMap();

  const usageMap =
    usage?.reduce(
      (acc, item) => {
        acc[item.service_alias_name] = acc[item.service_alias_name]
          ? mergeUsage(acc[item.service_alias_name]!, item)
          : { ...item, metrics: { ...item.metrics } };
        return acc;
      },
      {} as Record<string, MetricUsage>
    ) || {};

  return (
    usage && (
      <BreakdownBar
        segments={Object.values(usageMap).map((item) => {
          const size =
            metricKey === "cost" && getCost
              ? getCost(item)
              : getMetricValue(item, metricKey);
          return {
            size,
            overlay:
              overlayMetric && size > 0
                ? getMetricValue(item, overlayMetric.key) / size
                : undefined,
            tooltip: (
              <UsageTooltip
                usage={item}
                metricLabel={metricLabel}
                metricKey={metricKey}
                overlayMetric={overlayMetric}
                getCost={getCost}
              />
            ),
            borderColor: getColors(item.service_alias_name).borderColor,
            fillColor: getColors(item.service_alias_name).backgroundColor,
          };
        })}
      />
    )
  );
};

const sortUsage = (
  usage?: MetricUsage[],
  metricKey?: string,
  getCost?: (usage: MetricUsage[] | MetricUsage) => number
) =>
  usage?.toSorted((left, right) =>
    metricKey === "cost" && getCost
      ? getCost(right) - getCost(left)
      : getMetricValue(right, metricKey || "") -
        getMetricValue(left, metricKey || "")
  );

const getTotalUsage = (
  usage?: MetricUsage[],
  metricKey?: string,
  getCost?: (usage: MetricUsage[] | MetricUsage) => number
) =>
  metricKey === "cost" && getCost
    ? getCost(usage || [])
    : (usage || []).reduce(
        (sum, item) => sum + getMetricValue(item, metricKey || ""),
        0
      );

const LoadingBar = () => (
  <Placeholder
    as="div"
    animation="glow"
    className="d-flex align-items-end"
    style={{ height: "30px" }}
  >
    <Placeholder
      className="w-100 d-block"
      style={{ height: "24px", borderRadius: "3px" }}
    />
  </Placeholder>
);

const SummarySection = ({
  usage,
  title,
  scope,
  timestamp,
  metric,
  overlayMetric,
  getCost,
  isLoading,
}: {
  usage?: MetricUsage[];
  title: string;
  scope: string;
  timestamp?: string;
  metric: UsageMetricMetadata | { key: "cost"; display_name: string };
  overlayMetric?: OverlayMetric;
  getCost?: (usage: MetricUsage[] | MetricUsage) => number;
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <BarReportSectionContainer>
        <BarReportSectionHeader>
          <h6>
            <ReportLabel>{title}</ReportLabel>
          </h6>
          <h6>
            <Placeholder animation="glow">
              <Placeholder style={{ width: "4rem" }} />
            </Placeholder>
          </h6>
        </BarReportSectionHeader>
        <LoadingBar />
      </BarReportSectionContainer>
    );
  }

  const filteredUsage = usage?.filter(
    (item) =>
      item.scope === scope && (!timestamp || item.timestamp === timestamp)
  );
  const sortedUsage = sortUsage(filteredUsage, metric.key, getCost);
  const total = getTotalUsage(sortedUsage, metric.key, getCost);
  return (
    <BarReportSectionContainer>
      <BarReportSectionHeader>
        <h6>
          <ReportLabel>{title}</ReportLabel>
        </h6>
        <h6>
          <ReportLabel>
            {metric.key === "cost"
              ? formatCurrency(total)
              : formatNumber(total)}
          </ReportLabel>
        </h6>
      </BarReportSectionHeader>
      <UsageSummaryBar
        usage={sortedUsage}
        metricKey={metric.key}
        metricLabel={metric.display_name}
        overlayMetric={overlayMetric}
        getCost={getCost}
      />
    </BarReportSectionContainer>
  );
};

/**
 * Per-service usage bars for one metric. An overlayMetric, a sub-quantity of
 * the metric such as cached input tokens of input tokens, is shaded over the
 * leading part of each bar.
 */
const MetricUsageSummaryReport = ({
  usage,
  sections,
  metric,
  overlayMetric,
  getCost,
  isLoading,
}: {
  usage?: MetricUsage[];
  sections: SummarySection[];
  metric: UsageMetricMetadata | { key: "cost"; display_name: string };
  overlayMetric?: OverlayMetric;
  getCost?: (usage: MetricUsage[] | MetricUsage) => number;
  isLoading?: boolean;
}) => {
  return (
    <ReportSectionContainer>
      {sections.map(({ scope, timestamp, title }) => (
        <SummarySection
          key={`${metric.key}-${scope}-${timestamp}`}
          usage={usage}
          title={title}
          scope={scope}
          timestamp={timestamp}
          metric={metric}
          overlayMetric={overlayMetric}
          getCost={getCost}
          isLoading={isLoading}
        />
      ))}
    </ReportSectionContainer>
  );
};

export default MetricUsageSummaryReport;
